/**
 * The Ask dock's scopes: one small config per thing the center can show.
 *
 * The dock (`AskDock`) is the same everywhere; these decide what it is about.
 * Each scope names its durable thread (the session key), what the
 * header says it is scoped to, the opener, the composer's placeholder, and the
 * three chips over the composer. Session keys are stable: changing one strands
 * the threads people already have under the old key.
 */

export interface YagniRailScope {
  /** The session key that names this scope's durable thread. */
  key: string;
  /** What the header says the rail is scoped to, beside "@yagni". */
  label: string;
  placeholder: string;
  /** @yagni's static opener for an empty thread (never sent, no model turn). */
  opening: string;
  /**
   * The prompt chips under the thread (three, by convention). A chip that ends in "…" fills the composer
   * for the person to finish ("Start a ticket for …"); the rest send on click.
   */
  chips: readonly string[];
  /** The UNTRUSTED lookup hint carried with each message; the server re-derives every value. */
  scopeContext?: Record<string, unknown>;
  /**
   * A conversation the surface already owns, used instead of the key's thread.
   * `null` while it is still loading. Absent for every session-keyed scope.
   */
  conversationId?: string | null;
}

/** The creating verb: the chip fills the composer, the person names the ticket. */
export const START_A_TICKET_CHIP = "Start a ticket for …";

/** Work, the index. The one scope that starts tickets. */
export function workScope(): YagniRailScope {
  return {
    key: "work:surface",
    label: "your work",
    placeholder: "Ask, or start a ticket for …",
    opening:
      "Here to help with your work. Ask what needs you or what is running, or say \"start a ticket for …\" and I will start it on Work, in Plan. Paste a Linear or Jira ticket link or key, or a list of them, and it lands under Proposed.",
    chips: ["What needs me?", "What's running?", START_A_TICKET_CHIP],
  };
}

/**
 * One ticket, open in the case file. The lookup hint always names the ticket,
 * so the server grounds every turn in its state (the stage, the run, the open
 * ask, what can be done about it), not only at a plan gate or an open ask. At
 * an open plan gate the rail becomes the plan-review rail: the openers are
 * about the plan. The chat can do what the sheet's buttons do (ADR-0051), so
 * the first chip is the sheet's primary button when the chat can press it.
 */
export function ticketScope(
  ticket: { id: string; identifier: string },
  atPlanGate: boolean,
  /** The open gate carries an ask: the server grounds the turn in its record. */
  atOpenAsk = false,
  /** The sheet's primary button label, when the chat can do what it does (`primaryChipOf`). */
  primaryChip: string | null = null,
): YagniRailScope {
  const questions = atPlanGate
    ? ["Why this approach?", "What files does this touch?", "What does it leave out?"]
    : atOpenAsk
      ? ["What happened?", "What do you need from me?", "Which option would you pick?"]
      : ["What's the risk here?", "Why does this need me?", "What changed since the plan?"];
  return {
    key: `work:item:${ticket.id}`,
    label: `${ticket.identifier} · ${atPlanGate ? "plan review" : "this ticket"}`,
    placeholder: atPlanGate
      ? "Ask about the plan before you approve it…"
      : "Ask about this before you act…",
    opening: atPlanGate
      ? "Here to talk the plan through before you approve it. Ask why it takes this approach, what it touches, or what it leaves out, and I can propose changes for you to accept."
      : "Ask me about this ticket, or tell me what to do. I can do what the sheet's buttons do, and I check with you before anything starts.",
    chips: primaryChip ? [primaryChip, ...questions.slice(0, 2)] : questions,
    scopeContext: { workItemId: ticket.id },
  };
}

/** A Team's page. */
export function teamScope(teamId: string, teamName: string): YagniRailScope {
  return {
    key: `desk:${teamId}`,
    label: `${teamName} · this Team`,
    placeholder: `Ask about ${teamName}…`,
    opening: `Ask me about ${teamName}: what is stalled, what changed, which line is waiting on setup.`,
    chips: ["What's stalled?", "What changed this week?", "Which line needs setup?"],
  };
}

/** The decision ledger, or one decision on it. */
export function decisionsScope(
  decision: { id: string; decision: string; question: string; verification: unknown; repoFullName?: string | null } | null,
): YagniRailScope {
  if (!decision) {
    return {
      key: "decisions:surface",
      label: "the decision ledger",
      placeholder: "Ask about the decisions…",
      opening:
        "Ask me what the Teams decided about a topic, which calls are contested, and what is still an assumption.",
      chips: [
        "What did we decide about pricing?",
        "Which decisions are contested?",
        "What is still an assumption?",
      ],
    };
  }
  return {
    key: `decision:${decision.id}`,
    label: truncateLabel(decision.decision, 40),
    placeholder: "Ask about this decision…",
    opening:
      "Ask me whether this still holds, what leaned on it, or what it would take to change it.",
    chips: ["Is this still true?", "What relied on this decision?", "What would change it?"],
    scopeContext: {
      decisionId: decision.id,
      decisionQuestion: decision.question,
      decisionVerification: decision.verification,
      decisionRepo: decision.repoFullName ?? undefined,
    },
  };
}

/** Connections. */
export function connectionsScope(): YagniRailScope {
  return {
    key: "srf:integrations",
    label: "your connections",
    placeholder: "Ask about your connections…",
    opening: "Ask me what is behind, what each connector can do, or how to add a new one.",
    chips: ["What's behind?", "What can each connector do?", "How do I add a new one?"],
  };
}

function truncateLabel(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}
